// The one semantic color palette for chat entity types (caregiver, client,
// shift, callout) and for the sources a decision can cite (playbook, agency
// guide, care plan, memory). Both chat generations derive their entity
// visuals from here: the legacy pills in
// `components/phoebe_chat/entity_visuals.ts` and the v3 reference badges in
// `features/phoebe_chat_v3/ui/reference_icons.ts`. It lives in `lib/`
// because the v3 feature must not import `components/phoebe_chat/` (its
// import-boundary test enforces that), so the shared contract needs a
// renderer-neutral home. Change an entity's color here and every surface
// follows.

export type EntityTint = Readonly<{
    /** Chip background + text, e.g. an entity pill or mention badge. */
    readonly pill: string;
    /** Icon-only color, for icons outside a tinted chip. */
    readonly icon: string;
}>;

export const ENTITY_TINTS = {
    // Shift pills borrow the calendar's open-shift hue (the red/rose chips
    // in `calendar_model.ts`); in the v3 chat the status-aware calendar
    // palette (`shift_reference_palette.ts`) overrides this static tint.
    shift: { pill: 'bg-rose-50 text-rose-700', icon: 'text-rose-600' },
    // Caregiver and client pills use the calendar chips' construction — a
    // pastel tint with deep same-hue text, inverted in dark mode — so every
    // entity pill reads as one family. Slightly softer (200-level) than the
    // calendar's status tints, and the client lavender stays visibly apart
    // from the outreaching periwinkle (#C4C2FF).
    caregiver: {
        // Hand-tuned pastels: the stock Tailwind steps either washed out
        // or shouted next to the calendar's periwinkle shift pills.
        pill: 'bg-[#D9FAE6] text-green-950 dark:bg-green-950/70 dark:text-green-50',
        icon: 'text-green-600',
    },
    client: {
        pill: 'bg-[#F0E4FF] text-purple-950 dark:bg-purple-950/70 dark:text-purple-50',
        icon: 'text-purple-600',
    },
    callout: { pill: 'bg-amber-50 text-amber-700', icon: 'text-amber-600' },
    // A shift group belongs to an outreach and wears its tint; the Layers
    // icon is what tells the two apart.
    group: { pill: 'bg-amber-50 text-amber-700', icon: 'text-amber-600' },
    playbook: { pill: 'bg-teal-50 text-teal-700', icon: 'text-teal-600' },
    agency_guide: {
        pill: 'bg-indigo-50 text-indigo-700',
        icon: 'text-indigo-600',
    },
    care_plan: { pill: 'bg-rose-50 text-rose-700', icon: 'text-rose-600' },
    // Both memory types share one tint: the label reads "Memory · <name>"
    // either way, and the subject is what tells them apart.
    caregiver_memory: {
        pill: 'bg-fuchsia-50 text-fuchsia-700',
        icon: 'text-fuchsia-600',
    },
    client_memory: {
        pill: 'bg-fuchsia-50 text-fuchsia-700',
        icon: 'text-fuchsia-600',
    },
} as const satisfies Record<string, EntityTint>;

/** The one pill geometry for inline entity mentions, matching the v3 chat
 * badge (`reference_badge.tsx`): every surface that renders an @-mention
 * pill composes this with an `ENTITY_TINTS` (or calendar status) tint so
 * mentions read identically across the product. */
// my-[1.5px]: an atomic inline's margin box sets its line contribution, so
// the margin opens a small gap between pills stacked by line wrapping.
export const ENTITY_PILL_BASE_CLASSES =
    'my-[1.5px] inline-flex items-center gap-1 rounded-md px-1.5 py-px align-middle text-[12px] leading-[17px] font-medium whitespace-nowrap';
